import React from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  useTheme,
  alpha,
  Grid,
} from "@mui/material";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import CountUp from "react-countup";

const COLORS = [
  "#4F46E5", // indigo
  "#8B5CF6", // violet
  "#10B981", // emerald
  "#F59E0B", // amber
  "#EF4444", // red
  "#3B82F6", // blue
  "#EC4899", // pink
  "#14B8A6", // teal
];

function prettyNumber(num) {
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function ReceiptSummaryCards({
  monthlyTotal = 0,
  yearlyTotal = 0,
  categoryData = [],
}) {
  const theme = useTheme();
  const total = categoryData.reduce((sum, c) => sum + c.value, 0);

  const StatCard = ({ icon: Icon, title, amount, color, subtitle }) => (
    <Paper
      sx={{
        p: 3,
        borderRadius: 3,
        height: "100%",
        borderTop: `4px solid ${color}`,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography
          variant="body2"
          fontWeight={600}
          color="text.secondary"
          sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            bgcolor: alpha(color, 0.1),
            color: color,
          }}
        >
          <Icon fontSize="small" />
        </Box>
      </Box>
      <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: -1 }}>
        €<CountUp end={amount} decimals={2} duration={1} separator="," />
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Paper>
  );

  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={3}>
        {/* This Month */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            icon={CalendarMonthIcon}
            title="This Month"
            amount={monthlyTotal}
            color={theme.palette.error.main}
            subtitle="Total expenses"
          />
        </Grid>

        {/* This Year */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            icon={ReceiptLongIcon}
            title="This Year"
            amount={yearlyTotal}
            color={theme.palette.primary.main}
            subtitle="Year to date"
          />
        </Grid>

        {/* Average Monthly */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            icon={TrendingDownIcon}
            title="Monthly Average"
            amount={yearlyTotal / Math.max(new Date().getMonth() + 1, 1)}
            color={theme.palette.warning.main}
            subtitle="Based on YTD"
          />
        </Grid>
      </Grid>

      {/* Category Breakdown - Full Width */}
      {categoryData.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 3, mt: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={3}>
            Spending by Category
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  height: 250,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryData}
                      dataKey="value"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      innerRadius={55}
                      paddingAngle={2}
                      isAnimationActive={true}
                    >
                      {categoryData.map((entry, idx) => (
                        <Cell
                          key={`cell-${idx}`}
                          fill={COLORS[idx % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v, n) => [`€${prettyNumber(v)}`, n]}
                      contentStyle={{
                        fontSize: 14,
                        borderRadius: 8,
                        border: "none",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <Stack spacing={1.5}>
                {categoryData.slice(0, 8).map((entry, idx) => {
                  const percent = total ? (entry.value / total) * 100 : 0;
                  const color = COLORS[idx % COLORS.length];
                  return (
                    <Box key={entry.category}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={0.5}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              bgcolor: color,
                            }}
                          />
                          <Typography variant="body2" fontWeight={500}>
                            {entry.category}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Typography variant="body2" fontWeight={600}>
                            €{prettyNumber(entry.value)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ minWidth: 40, textAlign: "right" }}
                          >
                            {percent.toFixed(0)}%
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: alpha(color, 0.15),
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${percent}%`,
                            bgcolor: color,
                            borderRadius: 3,
                            transition: "width 0.5s ease",
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
}
